import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { ThemeToggle } from '../ThemeToggle';

const LanguageDropdown: React.FC<{
  language: string;
  onToggle: () => void;
}> = ({ language, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-text-secondary hover:text-brand-primary transition-colors p-1 rounded-md"
      >
        <img
          src={`https://flagicons.lipis.dev/flags/4x3/${language === 'en' ? 'gb' : 'de'}.svg`}
          alt={language === 'en' ? 'English' : 'Deutsch'}
          className="w-5 h-5 rounded-[0.125rem] preserve-color"
        />
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-bg-secondary rounded-md shadow-lg border border-border-primary overflow-hidden z-[1000]">
          <div className="py-1">
            <button
              onClick={() => {
                if (language !== 'en') {
                  onToggle();
                }
                setIsOpen(false);
              }}
              className={`flex items-center space-x-3 w-full px-4 py-2 text-sm text-text-secondary hover:text-brand-primary ${language === 'en' ? 'bg-hover-bg' : 'hover:bg-hover-bg'}`}
            >
              <img
                src="https://flagicons.lipis.dev/flags/4x3/gb.svg"
                alt="English"
                className="w-5 h-5 rounded-[0.125rem] preserve-color"
              />
              <span>English</span>
            </button>
            <button
              onClick={() => {
                if (language !== 'de') {
                  onToggle();
                }
                setIsOpen(false);
              }}
              className={`flex items-center space-x-3 w-full px-4 py-2 text-sm text-text-secondary hover:text-brand-primary ${language === 'de' ? 'bg-hover-bg' : 'hover:bg-hover-bg'}`}
            >
              <img
                src="https://flagicons.lipis.dev/flags/4x3/de.svg"
                alt="Deutsch"
                className="w-5 h-5 rounded-[0.125rem] preserve-color"
              />
              <span>Deutsch</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeaturesDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { language } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-text-secondary hover:text-brand-primary transition-colors"
      >
        <span>{t('nav.features')}</span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-48 bg-bg-secondary rounded-md shadow-lg border border-border-primary overflow-hidden z-[1000]">
          <div className="py-1">
            <Link
              to={`/${language}/pdf-to-xrechnung`}
              className="block px-4 py-2 text-sm text-text-secondary hover:text-brand-primary hover:bg-hover-bg"
              onClick={() => setIsOpen(false)}
            >
              {t('nav.pdfToXRechnung')}
            </Link>
            <Link
              to={`/${language}/xrechnung-to-pdf`}
              className="block px-4 py-2 text-sm text-text-secondary hover:text-brand-primary hover:bg-hover-bg"
              onClick={() => setIsOpen(false)}
            >
              {t('nav.xrechnungToPdf')}
            </Link>
            <Link
              to={`/${language}/xrechnung-viewer`}
              className="block px-4 py-2 text-sm text-text-secondary hover:text-brand-primary hover:bg-hover-bg"
              onClick={() => setIsOpen(false)}
            >
              {t('nav.xrechnungViewer')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'de' : 'en';
    setLanguage(newLang);
    
    // Update URL to reflect new language
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(/^\/(en|de)/, `/${newLang}`);
    navigate(newPath);
  };

  const handlePricingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <header style={{ backgroundColor: 'var(--bg-bg-primary)' }} className="border-b border-border-primary fixed top-0 left-0 right-0 z-50">
      <nav className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo - always visible */}
        <Link to="/" className="flex items-center space-x-2 text-brand-primary font-semibold text-xl hover:opacity-90 transition-colors">
          <Bot className="h-6 w-6 text-[var(--brand-primary)]" />
          <span className="text-sm sm:text-xl">Invoice-Converter.com</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          <FeaturesDropdown />
          <Link
            to={`/${language}/blog`}
            className="text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors"
          >
            {t('nav.blog')}
          </Link>
          <a
            href="#pricing"
            onClick={handlePricingClick}
            className="text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors"
          >
            {t('nav.pricing')}
          </a>
          {user ? (
            <Link
              to={`/${language}/profile`}
              className="text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors border border-border-secondary hover:border-brand-primary"
            >
              {t('nav.profile')}
            </Link>
          ) : (
            <Link
              to={`/${language}/sign-in`}
              className="text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors border border-border-secondary hover:border-brand-primary"
            >
              {t('nav.signin')}
            </Link>
          )}
          <Link
            to={user ? `/${language}/pdf-to-xrechnung` : `/${language}/sign-in`}
            className="bg-brand-primary text-button inline-flex items-center px-3 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-colors shadow-brand"
          >
            {t('nav.convert')}
          </Link>
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <LanguageDropdown language={language} onToggle={toggleLanguage} />
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <LanguageDropdown language={language} onToggle={toggleLanguage} />
          </div>
          <Link
            to={user ? `/${language}/pdf-to-xrechnung` : `/${language}/sign-in`}
            className="bg-brand-primary text-button px-3 py-1.5 rounded-md text-sm font-medium hover:opacity-90 transition-colors shadow-brand"
          >
            {t('nav.convertMobile')}
          </Link>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-text-secondary hover:text-brand-primary transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="absolute top-16 left-0 w-full bg-bg-primary border-t border-border-primary md:hidden">
          <div className="px-2 py-2 space-y-1">
            <div className="flex flex-col space-y-4">
              <FeaturesDropdown />
              <Link
                to="/pricing"
                onClick={handlePricingClick}
                className="text-text-secondary hover:text-brand-primary transition-colors"
              >
                {t('nav.pricing')}
              </Link>
            </div>
            <Link
              to={`/${language}/blog`}
              className="block text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.blog')}
            </Link>
            {user ? (
              <Link
                to={`/${language}/profile`}
                className="block text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.profile')}
              </Link>
            ) : (
              <Link
                to={`/${language}/sign-in`}
                className="block text-text-secondary hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.signin')}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;